import { useEffect, useState } from 'react'

import { Modal, ModalProvider } from '@joor/design-system'
import styled from 'styled-components'

import { toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import usePortal from 'hooks/usePortal'

import Actions from './components/Actions/Actions'
import GalleryView from './components/GalleryView/GalleryView'
import StyleData from './components/StyleData/StyleData'
import { useStyleData } from './manageImagesModal.hooks'
import { ModalIds } from './manageImagesModal.ids'
import messages from './manageImagesModal.messages'
import { Style } from './manageImagesModal.types'
import useStore from './store/useManageImagesStore'

export interface ManageImagesMetadata {
  styleId: string
  linesheetId: string
  linesheetName: string
}

interface ManageImagesModalProps extends ManageImagesMetadata {
  onClose: () => void
}

const StyledModal = styled(Modal)`
  max-height: 90vh;
  height: 90vh;

  &&& .disabled {
    background: transparent;
  }
`

const SectionContainer = styled.div`
  height: 100%;
  max-height: 90vh;
  display: grid;
  gap: 24px;

  &&& {
    padding: 0 24px;
  }

  @media (min-width: 768px) {
    grid-template-columns: 42% auto;
  }

  @media (min-width: 1280px) {
    max-width: calc(80vw - 48px);
    gap: 36px;
  }
`

const ManageImagesModal = ({
  styleId,
  linesheetId,
  linesheetName,
  onClose,
}: ManageImagesModalProps): JSX.Element | null => {
  const Portal = usePortal()

  const hardResetStore = useStore((state) => state.hardResetStore)
  const setProductId = useStore((state) => state.setProductId)
  const setLinesheetId = useStore((state) => state.setLinesheetId)
  const [style, setStyle] = useState<Style | null>(null)
  useStyleData(style, setStyle)

  const handleOnClose = () => {
    atlasClient.cache.evict({ fieldName: 'getManageImagesStyleData' })
    atlasClient.cache.evict({ fieldName: 'listUnmatchedUploads' })
    hardResetStore()
    onClose()
  }

  useEffect(() => {
    if (!styleId || !linesheetId) return
    setProductId({ productId: toGlobalId('Product', styleId) as string })
    setLinesheetId({
      linesheetId: toGlobalId('Collection', linesheetId) as string,
    })
  }, [styleId, linesheetId, setProductId, setLinesheetId])

  if (!styleId || !linesheetId) {
    return null
  }

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          id={ModalIds.Modal}
          title={{
            id: ModalIds.Title,
            text: `${messages.manageImages} | ${linesheetName}`,
          }}
          isOpen
          onClose={handleOnClose}
          closeIconId={ModalIds.CloseButton}
          size="extra-large"
          disableBackgroundClick
        >
          <Modal.Content>
            <SectionContainer>
              <StyleData style={style} />
              <GalleryView />
            </SectionContainer>
          </Modal.Content>
          <Modal.Actions>
            <Actions />
          </Modal.Actions>
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}

export default ManageImagesModal
